/**
 * Validação/normalização de URLs de redirecionamento configuradas pelo usuário
 * (Contagem Final Inteligente e Notificação de Vendas).
 *
 * Regra única, replicada nas 3 camadas (front, backend e player) — a
 * REFERÊNCIA é o backend (`controllers/videoController.js`,
 * `normalizeRedirectUrl`), porque é ele quem grava:
 *   - só `http://` e `https://` são aceitos;
 *   - uma URL sem esquema ("meusite.com/oferta") é normalizada para `https://`;
 *   - protocol-relative ("//meusite.com/oferta") é normalizada para
 *     `https://meusite.com/oferta` — há host, e o embed do player pode estar em
 *     http, então o esquema é fixado em https;
 *   - caminho relativo ("/oferta", sem host) é REJEITADO: não há o que
 *     normalizar e no player resolveria contra o domínio do CLIENTE (404);
 *   - qualquer outro esquema (`javascript:`, `data:`, `vbscript:`, `file:`,
 *     `blob:`, `about:`) é REJEITADO — vira `null`.
 *
 * O front usa isto para avisar quem configura; o backend rejeita/normaliza ao
 * gravar; o player sanitiza ao ler (documentos antigos podem ter URL ruim).
 */

// Qualquer coisa no formato `esquema:` no começo da string.
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_RE = /^https?:\/\//i;
// Caracteres de controle: `java\nscript:` é executável no navegador e passaria
// por um teste ingênuo de esquema, então saem antes de qualquer verificação.
// A regra `no-control-regex` está desligada só aqui: os caracteres de controle
// são exatamente o alvo desta expressão.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001F\u007F]/g;

/**
 * Devolve a URL normalizada e segura, ou `null` quando não há como usá-la.
 */
export function normalizeRedirectUrl(raw?: string | null): string | null {
  if (typeof raw !== 'string') return null;

  const url = raw.replace(CONTROL_RE, '').trim();
  if (!url) return null;

  let candidate: string;
  if (HTTP_RE.test(url)) {
    candidate = url;
  } else if (SCHEME_RE.test(url)) {
    // Tem esquema, mas não é http(s) → rejeitado.
    return null;
  } else if (url.startsWith('//')) {
    // Protocol-relative: tem host, só falta o esquema. Mesmo tratamento do
    // backend — fixa `https:` em vez de herdar o esquema da página do cliente.
    candidate = `https:${url}`;
  } else if (url.startsWith('/')) {
    // Caminho relativo ("/oferta"): não há host para normalizar, e o parser
    // colapsaria "https:///oferta" em host "oferta". Rejeitado, como no backend.
    return null;
  } else {
    candidate = `https://${url}`;
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * O que vai DEPOIS de `https://` na mensagem de autofix dos painéis
 * ("vamos salvar como https://{url}"). Só difere do texto cru no caso
 * protocol-relative: "//meusite.com/oferta" será gravado como
 * "https://meusite.com/oferta", então repetir as duas barras imprimiria
 * "https:////meusite.com/oferta" e a mensagem mentiria.
 */
export function getRedirectUrlAutofixSuffix(raw?: string | null): string {
  const url = (raw || '').replace(CONTROL_RE, '').trim();
  return url.startsWith('//') ? url.slice(2) : url;
}

export function isValidRedirectUrl(raw?: string | null): boolean {
  return normalizeRedirectUrl(raw) !== null;
}

export type RedirectUrlState =
  | 'empty'    // nada digitado
  | 'invalid'  // esquema proibido / não dá para virar http(s)
  | 'autofix'  // sem esquema: será salvo como https://...
  | 'valid';

/**
 * Estado visual do campo de URL nos painéis de configuração.
 */
export function getRedirectUrlState(raw?: string | null): RedirectUrlState {
  const url = (raw || '').trim();
  if (!url) return 'empty';
  const normalized = normalizeRedirectUrl(url);
  if (!normalized) return 'invalid';
  return HTTP_RE.test(url) ? 'valid' : 'autofix';
}
